import { useEffect, useRef } from "react";
import { ArrowIcon } from "./_shared";

// Ported from Claude Design handoff (Direction A: Sinal).
// Live radar canvas + floating event/score panels. Editorial dual-weight H1.

type RadarEvent = {
  time: string;
  domain: string;
  evt: string;
  ok: boolean;
};

const RADAR_EVENTS: RadarEvent[] = [
  { time: "14:22:03", domain: "app.cliente.com.br", evt: "SSL válido · 312d", ok: true },
  { time: "14:21:58", domain: "api.cliente.com.br", evt: "Uptime 200", ok: true },
  { time: "14:21:44", domain: "mail.cliente.com.br", evt: "Blacklist limpa", ok: true },
  { time: "14:21:30", domain: "cdn.cliente.com.br", evt: "Anomalia detectada", ok: false },
  { time: "14:21:12", domain: "auth.cliente.com.br", evt: "DNS estável", ok: true },
];

const HERO_STATS: Array<[string, string]> = [
  ["11+", "monitores"],
  ["24/7", "contínuo"],
  ["PT-BR", "IA em português"],
  ["<48h", "primeiro relatório"],
];

export function Hero() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const DPR = Math.min(2, window.devicePixelRatio || 1);
    const CW = 560;
    const CH = 560;
    canvas.width = CW * DPR;
    canvas.height = CH * DPR;
    canvas.style.width = CW + "px";
    canvas.style.height = CH + "px";
    ctx.scale(DPR, DPR);
    const cx = CW / 2;
    const cy = CH / 2;
    const R = 235;

    const dots = Array.from({ length: 16 }, (_, i) => ({
      angle: (i / 16) * Math.PI * 2 + Math.random() * 0.3,
      dist: 55 + Math.random() * (R - 65),
      size: 1.1 + Math.random() * 1.4,
      pulsePhase: Math.random() * Math.PI * 2,
    }));

    let raf = 0;
    const t0 = performance.now();

    const draw = () => {
      const t = (performance.now() - t0) / 1000;
      ctx.clearRect(0, 0, CW, CH);

      // concentric rings
      ctx.strokeStyle = "rgba(245,247,250,0.08)";
      ctx.lineWidth = 1;
      for (let i = 1; i <= 4; i++) {
        ctx.beginPath();
        ctx.arc(cx, cy, (R / 4) * i, 0, Math.PI * 2);
        ctx.stroke();
      }
      // crosshairs
      ctx.beginPath();
      ctx.moveTo(cx - R, cy);
      ctx.lineTo(cx + R, cy);
      ctx.moveTo(cx, cy - R);
      ctx.lineTo(cx, cy + R);
      ctx.stroke();

      // sweep
      const sweep = reduceMotion ? Math.PI * 0.3 : (t * 0.7) % (Math.PI * 2);
      const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, R);
      grad.addColorStop(0, "rgba(132,255,0,0.22)");
      grad.addColorStop(1, "rgba(132,255,0,0)");
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, R, sweep - 0.55, sweep);
      ctx.closePath();
      ctx.fill();

      ctx.strokeStyle = "rgba(132,255,0,0.55)";
      ctx.lineWidth = 1.4;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(cx + Math.cos(sweep) * R, cy + Math.sin(sweep) * R);
      ctx.stroke();

      dots.forEach((d) => {
        const x = cx + Math.cos(d.angle) * d.dist;
        const y = cy + Math.sin(d.angle) * d.dist;
        const delta = Math.abs(((sweep - d.angle) + Math.PI * 3) % (Math.PI * 2) - Math.PI);
        const near = Math.min(1, Math.max(0, 1 - (Math.PI - delta) * 1.8));
        const basePulse = 0.5 + 0.3 * Math.sin(t * 2 + d.pulsePhase);
        const it = Math.min(1, basePulse * 0.5 + near);

        ctx.fillStyle = `rgba(132,255,0,${it})`;
        ctx.beginPath();
        ctx.arc(x, y, d.size + near * 1.6, 0, Math.PI * 2);
        ctx.fill();
        if (near > 0.3) {
          ctx.strokeStyle = `rgba(132,255,0,${near * 0.38})`;
          ctx.beginPath();
          ctx.arc(x, y, 6 + near * 10, 0, Math.PI * 2);
          ctx.stroke();
        }
      });

      ctx.fillStyle = "rgba(132,255,0,0.95)";
      ctx.beginPath();
      ctx.arc(cx, cy, 4, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = "rgba(132,255,0,0.35)";
      ctx.beginPath();
      ctx.arc(cx, cy, 10, 0, Math.PI * 2);
      ctx.stroke();

      if (!reduceMotion) raf = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(raf);
  }, []);

  const GITHUB_URL = "https://github.com/haporfirio/cyphron";

  return (
    <section
      className="cy-hero-section"
      style={{
        position: "relative",
        padding: "120px 24px 140px",
        background:
          "radial-gradient(ellipse at 72% 38%, rgba(132,255,0,0.10), transparent 55%), var(--cy-bg)",
        overflow: "hidden",
      }}
    >
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "linear-gradient(rgba(245,247,250,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(245,247,250,0.025) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
          maskImage:
            "radial-gradient(ellipse at 50% 40%, #000 40%, transparent 80%)",
          WebkitMaskImage:
            "radial-gradient(ellipse at 50% 40%, #000 40%, transparent 80%)",
          pointerEvents: "none",
        }}
      />

      <div
        className="cy-hero-grid"
        style={{
          position: "relative",
          display: "grid",
          gridTemplateColumns: "1.05fr 1fr",
          gap: 80,
          alignItems: "center",
          maxWidth: 1360,
          margin: "0 auto",
        }}
      >
        {/* Left column */}
        <div>
          <h1
            style={{
              fontFamily: "var(--cy-fs-sans)",
              fontSize: "clamp(36px, 5.5vw, 68px)",
              lineHeight: 1.03,
              letterSpacing: "-0.035em",
              fontWeight: 500,
              margin: 0,
              color: "var(--cy-ink-100)",
              textWrap: "balance",
            }}
          >
            Segurança digital para sua empresa,
            <br />
            <em
              style={{
                fontStyle: "italic",
                fontWeight: 300,
                color: "var(--cy-ink-60)",
              }}
            >
              sem complexidade técnica.
            </em>
          </h1>
          <p
            style={{
              marginTop: 28,
              fontSize: 19,
              lineHeight: 1.5,
              color: "var(--cy-ink-80)",
              maxWidth: 540,
            }}
          >
            Monitoramento 24/7 com inteligência artificial. Relatórios que
            qualquer pessoa entende. Conformidade com a LGPD automatizada.
          </p>
          <div style={{ display: "flex", gap: 12, marginTop: 36 }}>
            <a
              href={GITHUB_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="cy-btn cy-btn--primary"
            >
              View on GitHub
              <span className="cy-arrow">
                <ArrowIcon size={14} color="#0A1500" />
              </span>
            </a>
          </div>

          <div
            className="cy-hero-stats"
            style={{
              marginTop: 56,
              paddingTop: 20,
              borderTop: "1px solid var(--cy-ink-10)",
              display: "grid",
              gridTemplateColumns: "repeat(4, auto)",
              gap: 40,
              justifyContent: "start",
            }}
          >
            {HERO_STATS.map(([v, l]) => (
              <div key={v}>
                <div
                  className="cy-mono"
                  style={{ fontSize: 18, color: "var(--cy-accent)" }}
                >
                  {v}
                </div>
                <div className="cy-tape" style={{ marginTop: 4 }}>
                  {l}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right column — radar + floating panels */}
        <div className="cy-hero-viz" style={{ position: "relative" }}>
          <canvas
            ref={canvasRef}
            style={{ display: "block", margin: "0 auto" }}
          />

          {/* Events panel */}
          <div
            style={{
              position: "absolute",
              right: -24,
              top: 30,
              width: 310,
              background: "rgba(13,19,32,0.78)",
              border: "1px solid var(--cy-ink-10)",
              borderRadius: "var(--cy-r-md)",
              backdropFilter: "blur(14px)",
              WebkitBackdropFilter: "blur(14px)",
              padding: "14px 16px",
              boxShadow: "0 20px 60px -20px rgba(0,0,0,0.6)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 12,
              }}
            >
              <span className="cy-tape cy-tape--accent">eventos · ao vivo</span>
              <span
                className="cy-mono"
                style={{ fontSize: 11, color: "var(--cy-ink-60)" }}
              >
                12.341
              </span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
              {RADAR_EVENTS.map((e, i) => (
                <div
                  key={i}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "56px 1fr auto",
                    gap: 10,
                    fontSize: 11.5,
                    alignItems: "center",
                    opacity: 1 - i * 0.11,
                  }}
                >
                  <span
                    className="cy-mono"
                    style={{ color: "var(--cy-ink-40)" }}
                  >
                    {e.time}
                  </span>
                  <span
                    className="cy-mono"
                    style={{
                      color: e.ok ? "var(--cy-ink-80)" : "var(--cy-warn)",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {e.domain}
                  </span>
                  <span
                    className="cy-mono"
                    style={{
                      fontSize: 10,
                      color: e.ok ? "var(--cy-accent)" : "var(--cy-warn)",
                    }}
                  >
                    {e.ok ? "● OK" : "● ALT"}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Score panel */}
          <div
            style={{
              position: "absolute",
              left: -16,
              bottom: 36,
              width: 240,
              background: "rgba(13,19,32,0.78)",
              border: "1px solid var(--cy-ink-10)",
              borderRadius: "var(--cy-r-md)",
              backdropFilter: "blur(14px)",
              WebkitBackdropFilter: "blur(14px)",
              padding: "14px 16px",
              boxShadow: "0 20px 60px -20px rgba(0,0,0,0.6)",
            }}
          >
            <span className="cy-tape">score de segurança</span>
            <div
              style={{
                display: "flex",
                alignItems: "baseline",
                gap: 4,
                marginTop: 8,
              }}
            >
              <span
                style={{
                  fontSize: 42,
                  fontWeight: 500,
                  letterSpacing: "-0.03em",
                  color: "var(--cy-accent)",
                }}
              >
                87
              </span>
              <span
                className="cy-mono"
                style={{ color: "var(--cy-ink-60)", fontSize: 13 }}
              >
                /100
              </span>
            </div>
            <div
              style={{
                height: 3,
                background: "var(--cy-ink-10)",
                marginTop: 10,
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: "87%",
                  height: "100%",
                  background: "var(--cy-accent)",
                  boxShadow: "0 0 8px var(--cy-accent-glow)",
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
